import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import { API_DATE_WITH_TIME_AND_TIMEZONE, MARKET_TIMEZONE } from '../constants/datePatterns';
import {
    AlgoStrategy,
    BondQuantityType,
    OfferingType,
    OrderFillType,
    OrderMarketTime,
    OrderTransaction,
    OrderType,
    OrderValidity,
    PriceType,
} from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import {
    Apis,
    CancelledCryptoOrderInfo,
    CryptoOrderHistoryWithExtraInfo,
    CryptoTradePreview,
    MiddlewareConfig,
    Model,
    ModifyOrderFormValues,
    Order,
    OrderWithExtraInfo,
} from '../types';
import {
    BasketOrderBody,
    BasketOrderValues,
    CryptoTradingFormValues,
    OfferingListing,
    OfferingOrderPreview,
    OfferingTradingFormValues,
    OrderPreview,
    OrderRef,
    TradingFormMeta,
    TradingFormValues,
} from '../types/Trading.type';
import { formatDate, formatOrders, multiply } from '../utils';
import type { QuoteApi } from './QuoteApi';
import type { UserApi } from './UserApi';

@injectable()
export class TradingApi extends BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(ApiInjectToken.UserApi) protected readonly userApi: UserApi,
        @inject(ApiInjectToken.QuoteApi) protected readonly quoteApi: QuoteApi,
    ) {
        super(apis, config);
    }

    protected prepareOrder(values: TradingFormValues, meta: TradingFormMeta, requireNbbo: boolean) {
        const {
            symbol,
            orderType,
            limitPrice,
            stopPrice,
            transaction,
            fillType,
            quantity,
            contracts,
            marketTime,
            validity,
            optionLegs,
            mutualFundValue,
            notionalValue,
            accountId,
            modelId,
            trailingStopPegType,
            trailingStopPegDifference,
            positionType,
            spreadPrice,
            price,
            ocoOrder,
            ocoQuantity,
            ocoSymbol,
            ocoLimitPrice,
            ocoStopPrice,
            algoStrategy,
            algoStartTime,
            algoEndTime,
            volumeConstraint,
            settlementCurrency,
            commission,
            expireTime,
        } = values;

        const {
            modelTrading,
            inventoryTrading,
            optionTrading,
            spreadTrading,
            mutualFundTrading,
            notionalOrder,
            notionalLimitOrder,
            limitOrder,
            stopOrder,
            trailingStopOrder,
            marketOrder,
            buyWrite,
            bondTrading,
            buying,
            algoTrading,
            shorting,
            buyToCover,
        } = meta;

        let orderBody: any;

        if (bondTrading) {
            orderBody = {
                account: {
                    accountId,
                },
                requireNbbo,
                order: {
                    quote: {
                        isin: symbol,
                    },
                    orderType: OrderType.Limit,
                    quantityType: BondQuantityType.Unit,
                    quantity,
                    transaction,
                    price,
                    priceType: PriceType.Percentage,
                    settlementCurrency,
                    commission,
                },
            };
        } else if (mutualFundTrading) {
            orderBody = {
                account: {
                    accountId,
                },
                requireNbbo,
                order: {
                    quote: {
                        symbol,
                    },
                    transaction,
                    settlementCurrency,
                    value: mutualFundValue,
                },
            };
        } else if (buyWrite && contracts) {
            orderBody = {
                account: {
                    accountId,
                },
                requireNbbo,
                order: {
                    validity,
                    orderType,
                    legs: [
                        {
                            quantity: multiply(contracts, 100),
                            quote: {
                                symbol: symbol,
                                option: false,
                            },
                            transaction: OrderTransaction.Buy,
                        },
                        {
                            quantity: contracts,
                            quote: {
                                symbol: optionLegs[0]?.symbol,
                                option: true,
                            },
                            transaction: OrderTransaction.SellToOpen,
                        },
                    ],
                },
            };
        } else if (spreadTrading) {
            orderBody = {
                account: {
                    accountId,
                },
                requireNbbo,
                order: {
                    validity,
                    orderType,
                    legs: optionLegs.map((leg) => {
                        return {
                            quantity: multiply(contracts || 0, leg.ratio ?? 1),
                            quote: {
                                symbol: leg.symbol,
                                option: true,
                            },
                            transaction: this.getOptionSpreadTransaction(
                                values.transaction || OrderTransaction.Open,
                                leg.action || OrderTransaction.Buy,
                            ),
                        };
                    }),
                },
            };

            if (!marketOrder) {
                orderBody.order.spreadPrice = spreadPrice;
            }
        } else {
            orderBody = {
                requireNbbo,
                order: {
                    quote: {
                        symbol,
                        option: optionTrading,
                    },
                    marketTime,
                    quantity,
                    orderAccountType: positionType,
                    transaction,
                    orderType,
                    fillType,
                    validity,
                    settlementCurrency,
                    commission,
                },
            };

            if (modelTrading) {
                orderBody.model = {
                    id: modelId,
                };
            } else if (inventoryTrading) {
                orderBody.inventory = true;
            } else {
                orderBody.account = {
                    accountId,
                };
            }

            if (optionTrading) {
                orderBody.order.quote.symbol = optionLegs[0]?.symbol;
                orderBody.order.marketTime = OrderMarketTime.CoreMarket;
                orderBody.order.quantity = contracts;
            }

            if (algoTrading) {
                orderBody.order.orderType = algoStrategy;
                orderBody.order.startTime =
                    algoStartTime &&
                    formatDate(algoStartTime, {
                        pattern: API_DATE_WITH_TIME_AND_TIMEZONE,
                        timeZone: MARKET_TIMEZONE,
                    });
                orderBody.order.endTime =
                    algoEndTime &&
                    formatDate(algoEndTime, {
                        pattern: API_DATE_WITH_TIME_AND_TIMEZONE,
                        timeZone: MARKET_TIMEZONE,
                    });

                if (algoStrategy !== AlgoStrategy.TWAP) {
                    orderBody.order.volumeConstraint = volumeConstraint;
                }

                if (limitOrder) {
                    orderBody.order.limitPrice = limitPrice;
                }
            }

            if (limitOrder || notionalLimitOrder) {
                orderBody.order.limitPrice = limitPrice;
            }

            if (stopOrder) {
                orderBody.order.stopPrice = stopPrice;
            }

            if (trailingStopOrder) {
                orderBody.order.pegType = trailingStopPegType;
                orderBody.order.pegDifference = trailingStopPegDifference;
            }

            // OCO orders are equity only
            if (ocoOrder) {
                orderBody.order.oco = {
                    quote: {
                        // Symbol must match when selling oco
                        symbol: buying ? ocoSymbol : symbol,
                        option: optionTrading,
                    },
                    // Sells must be same quantity as main order
                    quantity: buying ? ocoQuantity : quantity,
                };

                if (
                    (limitOrder && transaction === OrderTransaction.Buy) ||
                    (stopOrder && transaction === OrderTransaction.Sell)
                ) {
                    orderBody.order.oco.limitPrice = ocoLimitPrice;
                }

                if (
                    (stopOrder && transaction === OrderTransaction.Buy) ||
                    (limitOrder && transaction === OrderTransaction.Sell)
                ) {
                    orderBody.order.oco.stopPrice = ocoStopPrice;
                }
            }

            if (notionalOrder || notionalLimitOrder) {
                delete orderBody.order.quantity;
                orderBody.order.expectedValue = notionalValue;
            }

            if (shorting || buyToCover) {
                delete orderBody.order.orderAccountType;
            }

            if (values.validity === OrderValidity.GoodTilDate && expireTime) {
                orderBody.order.expireTime = formatDate(expireTime, {
                    pattern: API_DATE_WITH_TIME_AND_TIMEZONE,
                    timeZone: MARKET_TIMEZONE,
                });
            }
        }

        return orderBody;
    }

    private prepareCryptoOrder(values: CryptoTradingFormValues, accountNumber: string) {
        const { coin, orderType, quantity, limitPrice, transaction } = values;
        return {
            account: {
                accountNumber,
            },
            order: {
                orderType,
                quote: {
                    symbol: coin,
                },
                quantity,
                limitPx: limitPrice,
                transaction: transaction.toUpperCase(),
            },
        };
    }

    private prepareBasketOrder(accountId: number, values: BasketOrderValues[], requireNbbo: boolean): BasketOrderBody {
        return {
            requireNbbo,
            account: {
                accountId,
            },
            orders: values.map((item) => {
                const orderBody: any = {
                    quote: { symbol: item.symbol },
                    transaction: item.transaction,
                    marketTime: OrderMarketTime.CoreMarket,
                    orderType: item.orderType ?? OrderType.Market,
                    fillType: OrderFillType.Any,
                    validity: OrderValidity.GoodForDay,
                    quantity: item.quantity,
                    orderAccountType: item.positionType,
                };

                if (
                    item.orderType === OrderType.Limit ||
                    item.orderType === OrderType.StopLimit ||
                    item.orderType === OrderType.LimitOnClose ||
                    item.orderType === OrderType.LimitOnOpen
                ) {
                    orderBody.limitPrice = item.limitPrice;
                }
                if (item.orderType === OrderType.Stop || item.orderType === OrderType.StopLimit) {
                    orderBody.stopPrice = item.stopPrice;
                }

                return orderBody;
            }),
        };
    }

    private prepareOfferingsOrder(accountNumber: string, values: OfferingTradingFormValues) {
        return {
            account: {
                accountNumber,
            },
            order: {
                quote: {
                    identifier: values.id,
                },
                expectedValue: values.investmentAmount,
                rule5130And5131Attestation: values.rule5130And5131Attestation,
                type: values.type,
                vendorRef: values.subscriptionId,
            },
        };
    }

    private getOptionSpreadTransaction = (action: OrderTransaction, legAction: OrderTransaction) => {
        if (action === OrderTransaction.Open) {
            if (legAction === OrderTransaction.Buy) {
                return OrderTransaction.BuyToOpen;
            } else if (legAction === OrderTransaction.Sell) {
                return OrderTransaction.SellToOpen;
            } else {
                return legAction;
            }
        } else {
            if (legAction === OrderTransaction.Buy) {
                return OrderTransaction.BuyToClose;
            } else if (legAction === OrderTransaction.Sell) {
                return OrderTransaction.SellToClose;
            } else {
                return legAction;
            }
        }
    };

    async previewOrder(
        values: TradingFormValues,
        meta: TradingFormMeta,
        requireNbbo: boolean,
    ): Promise<OrderPreview | null> {
        const orderBody = this.prepareOrder(values, meta, requireNbbo);
        const {
            modelTrading,
            inventoryTrading,
            mutualFundTrading,
            optionTrading,
            spreadTrading,
            buyWrite,
            bondTrading,
            algoTrading,
        } = meta;

        let response;

        if (algoTrading) {
            response = await this.apis.orbis.orders.v2.equities.algoPreview(orderBody);
        } else if (bondTrading) {
            response = await this.apis.orbis.orders.v2.fixedIncome.bondOrderPreview(orderBody);
        } else if (mutualFundTrading) {
            response = await this.apis.orbis.orders.v2.mutualFunds.mutualFundPreview(orderBody);
        } else if (optionTrading) {
            if (spreadTrading || buyWrite) {
                response = await this.apis.orbis.orders.v2.options.previewMultileg(orderBody);
            } else {
                response = await this.apis.orbis.orders.v2.options.preview(orderBody);
            }
        } else if (modelTrading || inventoryTrading) {
            response = await this.apis.orbis.orders.v2.equities.advisoryPreview(orderBody);
        } else {
            response = await this.apis.orbis.orders.v2.equities.preview(orderBody);
        }

        return response.data;
    }

    async placeOrder(
        values: TradingFormValues,
        meta: TradingFormMeta,
        requireNbbo: boolean,
        clientSourceId?: string,
    ): Promise<OrderRef> {
        const orderBody = this.prepareOrder(values, meta, requireNbbo);
        orderBody.order.clientSourceId = clientSourceId;
        const {
            modelTrading,
            inventoryTrading,
            mutualFundTrading,
            optionTrading,
            spreadTrading,
            buyWrite,
            bondTrading,
            algoTrading,
        } = meta;

        let response;

        if (algoTrading) {
            response = await this.apis.orbis.orders.v2.equities.algoPlacement(orderBody);
        } else if (bondTrading) {
            response = await this.apis.orbis.orders.v2.fixedIncome.bondPlacement(orderBody);
        } else if (mutualFundTrading) {
            response = await this.apis.orbis.orders.v2.mutualFunds.mutualFundPlacement(orderBody);
        } else if (optionTrading) {
            if (spreadTrading || buyWrite) {
                response = await this.apis.orbis.orders.v2.options.placementMultileg(orderBody);
            } else {
                response = await this.apis.orbis.orders.v2.options.placement(orderBody);
            }
        } else if (modelTrading || inventoryTrading) {
            response = await this.apis.orbis.orders.v2.equities.advisoryPlacement(orderBody);
        } else {
            response = await this.apis.orbis.orders.v2.equities.placement(orderBody);
        }

        return { orderRef: response.data.orderRef || response.data.OrderRef, market: response.data.market };
    }

    async previewBasketOrder(accountId: number, values: BasketOrderValues[], requireNbbo: boolean) {
        const orderBody = this.prepareBasketOrder(accountId, values, requireNbbo);
        const response = await this.apis.orbis.orders.v2.equities.basketPreview(orderBody);
        return response.data;
    }

    async placeBasketOrder(accountId: number, values: BasketOrderValues[], requireNbbo: boolean) {
        const orderBody = this.prepareBasketOrder(accountId, values, requireNbbo);
        const response = await this.apis.orbis.orders.v2.equities.basketPlacement(orderBody);
        return response.data;
    }

    async previewOfferingsOrder(
        accountNumber: string,
        values: OfferingTradingFormValues,
    ): Promise<OfferingOrderPreview> {
        const orderBody = this.prepareOfferingsOrder(accountNumber, values);
        const response = await this.apis.orbis.orders.v2.offerings.participationPreview(orderBody);
        return response.data;
    }

    async placeOfferingsOrder(accountNumber: string, values: OfferingTradingFormValues) {
        const orderBody = this.prepareOfferingsOrder(accountNumber, values);
        const response = await this.apis.orbis.orders.v2.offerings.participate(orderBody);
        return response.data;
    }

    async getOfferingListing(account: string, type: OfferingType): Promise<OfferingListing[]> {
        const response = await this.apis.orbis.orders.v2.offerings.offeringListing({
            account,
            type,
        });

        return response.data;
    }

    async modifyOrder({
        values,
        accountId,
        accountNumber,
        marketCode,
    }: {
        values: ModifyOrderFormValues;
        accountId: number;
        accountNumber: string;
        marketCode: string;
    }): Promise<string> {
        const response = await this.apis.orbis.orders.v2.cancelReplace({
            account: { accountId, accountNumber },
            order: { ...values, marketCode },
        });
        return response.data.orderRef || response.data.OrderRef || '';
    }

    async cancelOrder(orderRef: string, accountId: number, marketCode: string): Promise<string> {
        const response = await this.apis.orbis.orders.v2.cancellation({
            // You need accountId OR accountNumber. accountNumber doesn't exist on open order data (orders/open), therefor we default to using accountId here for simplicity
            account: { accountId },
            order: { orderRef, marketCode },
        });
        return response.data.orderRef || response.data.OrderRef || '';
    }

    async previewCryptoOrder(values: CryptoTradingFormValues, accountNumber: string): Promise<CryptoTradePreview> {
        const response = await this.apis.orbis.orders.v2.crypto.orderPreview(
            this.prepareCryptoOrder(values, accountNumber),
        );
        return response.data;
    }

    async placeCryptoOrder(values: CryptoTradingFormValues, accountNumber: string): Promise<OrderWithExtraInfo> {
        const response = await this.apis.orbis.orders.v2.crypto.orderPlace(
            this.prepareCryptoOrder(values, accountNumber),
        );
        return response.data;
    }

    async getOrderDetails(accountId: number, orderRef: string, market?: string): Promise<OrderWithExtraInfo | null> {
        const data = await Promise.all([
            this.config.apiPermissions.advisor
                ? this.apis.orbis.advisory.models.listing()
                : Promise.resolve({ data: [] }),
            this.config.apiPermissions.advisor
                ? this.userApi.getUserInfo() // we need accounts only for Oneview
                : Promise.resolve({ accountList: [] }),
            this.apis.orbis.orders.status(orderRef, {
                accountId,
                loadQuotes: true,
                bondPriceType: PriceType.Percentage,
                bondQuantityType: BondQuantityType.Unit,
                market,
            }),
        ]);

        const models = data[0].data as Model[];
        const accounts = data[1].accountList;
        const order = data[2].data as Order | null;

        if (!order) {
            return null;
        }

        return formatOrders({ orders: [order], models, accounts, combineOptionLegs: false })[0]!;
    }

    async getTradableCurrencyList(): Promise<string[]> {
        const response = await this.apis.orbis.miscellaneous.currencyList();
        return response.data;
    }

    async getOptionMultilegStrategy(values: TradingFormValues, meta: TradingFormMeta, requireNbbo: boolean) {
        const orderBody = this.prepareOrder(values, meta, requireNbbo);
        const response = await this.apis.orbis.orders.v2.options.multilegStrategy(orderBody);
        return response.data;
    }

    async getCryptoOrderDetails(accountId: number, orderRef: string): Promise<OrderWithExtraInfo | null> {
        return this.getOrderDetails(accountId, orderRef);
    }

    getCryptoOrderStatus(_accountId: string, _requestId: string): Promise<CryptoOrderHistoryWithExtraInfo> {
        throw new Error('Not supported');
    }

    cancelCryptoOrder(_accountNumber: string, _orderId: string): Promise<CancelledCryptoOrderInfo> {
        throw new Error('Not supported');
    }
}
